import { utils } from 'ethers'
import { ethers } from 'ethers'
/**
 * @param {string} addressA
 * @param {string} addressB
 * @returns {boolean}
 *
 * ## Address Equality
 *
 * ### The problem
 *
 * This function compares two addresses, because in certain conditions
 * the address characters could be upper or lower cased for the same address.
 *
 * An example of this issue in metamask can be seen here.
 *
 * https://github.com/MetaMask/metamask-extension/issues/10671
 *
 * ### An example
 *
 * If a user creates a proposal and signs it with the address
 * ```
 * 0x83e6Be7397b52143aD60F775A267015a640D671A
 * ```
 * it is stored in the smart contract under that creator_address.
 *
 * If a user goes to "Mine" proposals, they will recieve an all lower case address from MM:
 * ```
 * 0x83e6be7397b52143ad60f775a267015a640d671a
 * ```
 *
 * So when we compare the two addresses, they are not equal and no proposals appear.
 *
 * ### The solution
 *
 * We use ethers.js on each address to convert them both to the checksum address.
 * This ensures that the addresses are compared in a case insensitive manner.
 */
export function addressesEqual(addressA, addressB) {
  return utils.getAddress(addressA) === utils.getAddress(addressB)
}

/** @param {string} walletAddress */
export function truncateWalletAddress(walletAddress) {
  return `${walletAddress?.slice(0, 6)}...${walletAddress?.slice(-4)}`
}

/** @param {string} walletAddress */
export function validateWalletAddress(walletAddress) {
  if (!walletAddress) {
    console.error(
      'crypto.js: validateWalletAddress: walletAddress is required, received:',
      walletAddress
    )
    return false
  }

  return !!walletAddress.match(/^0x[a-fA-F0-9]{40}$/)
}

/** @param {number} n */
export function setCryptoValueDisplay(n) {
  if (typeof n == 'string') n = +n

  let str = n.toFixed(4)
  str = str.indexOf('.') !== -1 ? str.replace(/0+$/, '') : str
  str = str.indexOf('.') !== -1 ? str.replace(/\.$/, '') : str
  let parts = str.split('.')
  if (parts.length < 2) {
    str += '.00'
  } else if (parts[1].length === 1) {
    str += '0'
  }
  return str
}

/**
 *
 * @param {WalletAccount} a
 * @param {WalletAccount} b
 * @returns {boolean}
 */
export function accountsAreEqual(a, b) {
  return (
    a.role === b.role &&
    addressesEqual(a.walletAddress, b.walletAddress) &&
    addressesEqual(a.daoAddress, b.daoAddress) &&
    addressesEqual(a.erc20Address, b.erc20Address)
  )
}

/**
 * @param {number} sellAmount
 * @param {string} address
 */
export async function getGasPrice(sellAmount, address) {
  if (sellAmount === 0) return 0
  try {
    const provider = new ethers.providers.Web3Provider(window.ethereum)
    const gasPrice = provider.getGasPrice()
    const transaction = {
      to: address,
      value: ethers.utils.parseEther(sellAmount.toString()),
      gasPrice: gasPrice,
    }

    const estimatedGas = await provider.estimateGas(transaction)
    const estimatedGasGwei = ethers.utils.formatUnits(estimatedGas, 'gwei')
    return estimatedGasGwei.toString()
  } catch (error) {
    //  console.error('Insufficient Funds', error.message)
    // window.dispatchEvent(new CustomEvent('insufficientFunds'))
    return 0
  }
}

/**
 * @param {ethers.BigNumberish} n
 */
export function convertToEth(n) {
  try {
    const balanceAsEth = ethers.utils.formatEther(n)
    const balanceAsEthNumber = parseFloat(balanceAsEth)
    return balanceAsEthNumber
  } catch (e) {
    console.error(e)
  }
}

export function isZeroBalance() {
  return window.Globals.wallets.some((wallet) =>
    wallet.accounts.some((account) => account.balance <= 0)
  )
}
